import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Loader2, RefreshCw, AlertCircle, ChefHat, Wine } from 'lucide-react';
import { odooAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { format, subDays } from 'date-fns';

const FOOD_KEYWORDS = ['cold mezzes', 'desserts', 'hot mezzes', 'menus', 'main courses', 'salades'];
const DRINK_KEYWORDS = ['boissons', 'cave', 'cocktails', 'kamakian epicerie', 'vin au verre', 'vin'];

const isFood = (category) => {
  const cat = (category || '').toLowerCase();
  return FOOD_KEYWORDS.some(k => cat.includes(k));
};

const isDrink = (category) => {
  const cat = (category || '').toLowerCase();
  return DRINK_KEYWORDS.some(k => cat.includes(k));
};

const ProductionSection = ({ title, icon: Icon, items, color, borderColor }) => {
  if (items.length === 0) return null;
  return (
    <div className={`bg-white border ${borderColor} rounded-2xl overflow-hidden`}>
      <div className="px-4 md:px-6 py-4 flex items-center gap-3 border-b border-gray-100">
        <div className={`w-10 h-10 ${color} rounded-xl flex items-center justify-center`}>
          <Icon className="text-white" size={20} />
        </div>
        <div>
          <h2 className="font-heading font-bold text-lg text-gray-900">{title}</h2>
          <p className="text-gray-400 text-xs">{items.length} articles</p>
        </div>
      </div>
      <div className="divide-y divide-gray-100">
        {items.map((product, idx) => (
          <motion.div
            key={product.product_id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.02 }}
            className="px-4 md:px-6 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors"
            data-testid={`production-item-${product.product_id}`}
          >
            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
              idx < 3 ? 'bg-neon-pink/10 text-neon-pink border border-neon-pink/30' :
              idx < 10 ? 'bg-amber-50 text-amber-700 border border-amber-200' :
              'bg-gray-100 text-gray-500 border border-gray-200'
            }`}>
              {idx + 1}
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-gray-900 truncate">{product.product_name}</p>
              <p className="text-xs text-gray-400">{product.category}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <span className="font-heading text-lg font-bold text-neon-pink">{product.daily_average}</span>
              <span className="text-xs text-gray-400 ml-1">/jour</span>
            </div>
            <Badge className="bg-gray-100 text-gray-500 border border-gray-200 text-xs flex-shrink-0">
              {product.total_qty.toFixed(0)} total
            </Badge>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const ProductionPage = () => {
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [periodInfo, setPeriodInfo] = useState(null);
  const [days, setDays] = useState(7);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const dateFrom = format(subDays(new Date(), days), 'yyyy-MM-dd');
      const dateTo = format(new Date(), 'yyyy-MM-dd');
      const response = await odooAPI.getSalesAnalysis({ date_from: dateFrom, date_to: dateTo });
      setSalesData(response.data.data || []);
      setPeriodInfo(response.data.period);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const foodItems = salesData.filter(p => isFood(p.category)).slice(0, 20);
  const drinkItems = salesData.filter(p => isDrink(p.category)).slice(0, 20);

  return (
    <div className="min-h-screen p-3 md:p-8 pb-24 md:pb-8" data-testid="production-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pr-10 md:pr-0">
          <div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-gray-900 tracking-tight mb-1">
              Production
            </h1>
            <p className="text-gray-500 text-sm">
              Previsions basees sur {periodInfo?.num_days || days} jours de ventes Odoo
            </p>
          </div>
          <Button variant="outline" size="icon" onClick={fetchData}
            className="rounded-xl bg-white border-gray-200 text-gray-500 hover:bg-gray-100" data-testid="refresh-production-btn">
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>

        {/* Period selector */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { label: '7 jours', value: 7 },
            { label: '14 jours', value: 14 },
            { label: '30 jours', value: 30 },
            { label: '90 jours', value: 90 },
          ].map(p => (
            <Button key={p.value} variant="outline" size="sm"
              onClick={() => setDays(p.value)}
              className={`rounded-full ${days === p.value ? 'bg-neon-pink/10 text-neon-pink border-neon-pink/30' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
              {p.label}
            </Button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-neon-pink animate-spin mb-4" />
            <p className="text-gray-500">Chargement des donnees...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={fetchData} variant="outline" className="border-red-300 text-red-600">Reessayer</Button>
          </div>
        ) : (
          <div className="space-y-6">
            <ProductionSection
              title="Cuisine"
              icon={ChefHat}
              items={foodItems}
              color="bg-neon-pink"
              borderColor="border-neon-pink/20"
            />
            <ProductionSection
              title="Boissons & Cave"
              icon={Wine}
              items={drinkItems}
              color="bg-purple-500"
              borderColor="border-purple-200"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductionPage;
